// Qwen Service (Alibaba) via Ollama Cloud
// All API calls go through the server proxy for credit tracking

import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import type { ChatMessage, ConversationContext } from '../types/api';

interface QwenResponse {
  content: string;
  context: ConversationContext;
  type?: 'text';
}

class QwenService {
  private apiBase: string;

  constructor() {
    this.apiBase = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    logger.info('[QwenService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  isConfigured(): boolean {
    return typeof window !== 'undefined';
  }

  async generateResponseStream(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    onToken?: (delta: string) => void,
    model: string = 'qwen-3.5',
    imageUrls?: string[]
  ): Promise<QwenResponse> {
    const { data: { user } } = await supabase.auth.getUser();

    const contextMessages: ChatMessage[] = [];
    if (context?.messages) {
      for (const msg of context.messages) {
        if (typeof msg.content === 'string') {
          contextMessages.push(msg);
        }
      }
    }

    const streamHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (user?.id) {
      streamHeaders['x-user-id'] = user.id;
    }

    const res = await fetch(`${this.apiBase}/api/qwen/chat/stream`, {
      method: 'POST',
      headers: streamHeaders,
      body: JSON.stringify({
        prompt,
        systemPrompt,
        model,
        imageUrls,
        context: { messages: contextMessages },
      }),
    });

    if (!res.ok) {
      const errorBody = await res.text();
      logger.error('[QwenService] Stream request failed:', res.status, errorBody);
      throw new Error(`Qwen API error: ${res.status} - ${errorBody}`);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);

        if (!raw || raw.startsWith(':')) continue;

        const lines = raw.split('\n');
        let event: string | null = null;
        let dataStr = '';

        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }

        if (!dataStr) continue;

        try {
          const payload = JSON.parse(dataStr);
          if (event === 'error') {
            throw new Error(payload.message || 'Qwen stream error');
          } else if (event === 'done' && typeof payload.content === 'string') {
            fullContent = payload.content;
          } else if (typeof payload.delta === 'string') {
            fullContent += payload.delta;
            onToken?.(payload.delta);
          }
        } catch (parseErr) {
          if (parseErr instanceof SyntaxError) continue; // Ignore malformed chunks
          throw parseErr;
        }
      }
    }

    const allMessages: ChatMessage[] = [
      ...contextMessages,
      { role: 'user', content: prompt },
      { role: 'assistant', content: fullContent },
    ];

    return {
      content: fullContent,
      context: { messages: allMessages },
      type: 'text',
    };
  }
}

const qwenService = new QwenService();
export default qwenService;
